import { getLocalizedTitle, getLocalizedDescription } from '@immopersian/common';
import { useTranslation } from 'react-i18next';
import { X, Check, Crown, BookOpen, Tag, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Course, CourseSession } from '@immopersian/common';

interface DiscountInfo {
  code: string;
  percentOff: number;
  amountOff: number;
  formattedAmountOff: string;
  finalPrice: number;
  formattedFinalPrice: string;
  name: string;
}

interface PriceData {
  price: number;
  currency: string;
  formatted: string;
  hasDiscount: boolean;
  discount?: DiscountInfo;
  finalPrice?: number;
  formattedFinalPrice?: string;
}

interface PurchasePopupProps {
  course: Course & { sessions?: CourseSession[] };
  onClose: () => void;
  onPurchase: () => void;
  isLoading: boolean;
  priceData?: PriceData;
}

export function PurchasePopup({ 
  course, 
  onClose, 
  onPurchase,
  isLoading,
  priceData 
}: PurchasePopupProps) {
  const { t, i18n } = useTranslation();
  
  const totalSessions = course.sessions?.length || 0;
  const freeSessions = course.sessions?.filter(s => s.is_free).length || 0;
  
  const hasDiscount = priceData?.hasDiscount && priceData?.discount;
  const displayPrice = hasDiscount ? priceData.formattedFinalPrice : priceData?.formatted;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Close button - absolute positioned */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Two-column layout */}
        <div className="flex flex-col md:flex-row max-h-[90vh]">
          {/* Left side - Title and Description */}
          <div className="flex-1 p-6 overflow-y-auto">
            {/* Premium badge */}
            <div className="inline-flex items-center gap-1 bg-brand-terracotta text-white px-3 py-1 rounded-full text-sm font-medium mb-4">
              <Crown className="w-4 h-4" />
              {t('courses.premiumCourse').split(' - ')[0]}
            </div>

            <h2 className="text-2xl font-bold text-brand-dark mb-3">
              {getLocalizedTitle(course, i18n.language)}
            </h2>
            
            {/* Course stats */}
            <div className="flex items-center gap-4 text-sm text-brand-dark/70 mb-4">
              <div className="flex items-center gap-1">
                <BookOpen className="w-4 h-4" />
                {totalSessions} {t('courses.sessions')}
              </div>
              {freeSessions > 0 && (
                <div className="flex items-center gap-1 text-green-600">
                  <Check className="w-4 h-4" />
                  {freeSessions} {t('courses.freeSessions')}
                </div>
              )}
            </div>
            
            {/* Course description */}
            <div className="prose prose-sm prose-gray max-w-none mb-6 text-brand-dark/80">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {getLocalizedDescription(course, i18n.language)}
              </ReactMarkdown>
            </div>
            
            {/* What you get */}
            <div className="bg-brand-beige/50 rounded-xl p-4">
              <p className="font-semibold text-brand-dark mb-3">{t('courses.whatYouGet')}</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-3 text-sm text-brand-dark/80">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-green-600" />
                  </div>
                  {t('courses.fullAccess')}
                </li>
                <li className="flex items-center gap-3 text-sm text-brand-dark/80">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-green-600" />
                  </div>
                  {t('courses.lifetimeAccess')}
                </li>
                <li className="flex items-center gap-3 text-sm text-brand-dark/80">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-green-600" />
                  </div>
                  {t('courses.updatesIncluded')}
                </li>
              </ul>
            </div>
          </div>

          {/* Right side - Thumbnail, Price, and Purchase */}
          <div className="md:w-80 lg:w-96 bg-brand-beige/30 flex flex-col">
            {/* Thumbnail */}
            <div className="relative">
              {course.thumbnail_url ? (
                <div className="aspect-video w-full overflow-hidden">
                  <img 
                    src={course.thumbnail_url} 
                    alt={getLocalizedTitle(course, i18n.language)}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-video w-full bg-gradient-to-br from-brand-terracotta to-brand-dark flex items-center justify-center">
                  <Crown className="w-12 h-12 text-white/80" />
                </div>
              )}
              
              {/* Discount badge */}
              {hasDiscount && (
                <div className="absolute top-3 left-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1.5 shadow-lg animate-pulse">
                  <Sparkles className="w-4 h-4" />
                  {priceData.discount!.percentOff}% OFF
                </div>
              )}
            </div>

            {/* Price and Purchase section */}
            <div className="p-5 flex flex-col flex-1 justify-between">
              {/* Price display */}
              {priceData && (
                <div className="mb-4">
                  {hasDiscount ? (
                    <div className="text-center">
                      {/* Discount banner */}
                      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-full px-3 py-1 mb-3">
                        <Tag className="w-3.5 h-3.5 text-green-600" />
                        <span className="text-xs font-medium text-green-700">
                          {t('courses.saveAmount', { amount: priceData.discount!.formattedAmountOff })}
                        </span>
                      </div>
                      
                      {/* Prices */}
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="text-lg text-gray-400 line-through">
                          {priceData.formatted}
                        </span>
                        <span className="text-2xl font-bold text-green-600">
                          {priceData.formattedFinalPrice}
                        </span>
                      </div>
                      <span className="text-xs text-brand-dark/60">{t('courses.oneTimePayment')}</span>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-brand-dark mb-1">{priceData.formatted}</div>
                      <span className="text-xs text-brand-dark/60">{t('courses.oneTimePayment')}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Buttons */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={onPurchase}
                  disabled={isLoading}
                  className={`w-full px-5 py-3.5 text-white rounded-xl font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${
                    hasDiscount 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-lg shadow-green-500/25' 
                      : 'bg-brand-terracotta hover:bg-brand-terracotta-dark'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      {hasDiscount ? <Sparkles className="w-4 h-4" /> : <Crown className="w-4 h-4" />}
                      {t('courses.purchaseButton')} {displayPrice && `- ${displayPrice}`}
                    </>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="w-full px-5 py-2.5 text-brand-dark hover:bg-brand-beige rounded-xl font-medium transition-colors text-sm"
                >
                  {freeSessions > 0 ? t('courses.continueFreePreview') : t('courses.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
